import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Modal,
  Alert,
} from 'react-bootstrap';
import get from 'lodash/get';
import {
  getStartOfDayTZ,
  isBeforeTZ,
  createDateAndAddDays,
  formatForReservationToolTip,
} from '../../../../../../../modules/datetime';
import DateInput from '../../../../../../../src/web/components/form/inputs/DateInput/DateInput';
import BasicTenantForm from '../../../../BasicTenantForm';
import Button from '../../../../../../../modules/react/components/stateless/Button';
import Form from '../../../../../../../modules/react/components/stateful/Form';
import Input from '../../../../../../../modules/react/components/stateful/Input';
import SelectInput from '../../../../../../../modules/react/components/stateful/SelectInput';
import Field from '../../../../../../../modules/react/components/stateful/Field';
import { getCountryInfo, getCountries } from '../../../../../../../src/web/utils/geography';
import CurrentPageContext from '../../../../../../contexts/CurrentPageContext';
import {
  createValidator,
  email,
} from '../../../../../../../src/web/utils/validation';

import styles from '../Actions.scss';
import UnitDescription from './UnitDescription';

import AmountDueAtMoveIn from './AmountDueAtMoveIn';
import AmountDueToday from './AmountDueToday';
import { dataTrackClick } from '../../../../../../../src/web/utils/dataLayer';

export default class ReservationForm extends React.Component {
  static propTypes = {
    facility: PropTypes.shape({
      address: PropTypes.shape({
        country: PropTypes.string.isRequired,
      }),
      displayName: PropTypes.string.isRequired,
      settings: PropTypes.shape({
        softwareProvider: PropTypes.string,
        voyagerOnly: PropTypes.bool,
        hidePricingDisplayCall: PropTypes.bool,
        enablePublicDescription: PropTypes.bool,
      }),
      timezone: PropTypes.string,
      discountPlans: PropTypes.any,
    }).isRequired,
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    discountedPrice: PropTypes.number,
    discountPlans: PropTypes.arrayOf(PropTypes.shape({
      autoApply: PropTypes.bool,
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    appliedPromos: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
    })),
    isSubmitting: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.any,
    onSubmit: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    addEvent: PropTypes.func,
    userErrors: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string,
      }),
    ),
  };

  static defaultProps = {
    discountedPrice: null,
    discountPlans: [],
    appliedPromos: [],
    error: undefined,
    userErrors: undefined,
    addEvent: undefined,
  };

  static contextType = CurrentPageContext;

  state = {
    moveInDate: new Date().toISOString(),
    selectedPlan: null,
    // eslint-disable-next-line react/destructuring-assignment
    ...(getCountryInfo(this.props.facility.address.country)),
  };

  handleSubmit = (data) => {
    const { moveInDate, ...tenant } = data;
    tenant.phone = data.phone?.replace(/\D/g, '');
    const {
      discountPlans, onSubmit, facility, tierId, id, unitGroupId, addEvent,
    } = this.props;

    const { selectedPlan } = this.state;
    const discountPlanId = selectedPlan ? selectedPlan.id : null;
    const autoApplyDiscountPlanIds = [];
    discountPlans.forEach((plan) => {
      if (plan.autoApply) {
        autoApplyDiscountPlanIds.push(plan.id);
      }
    });
    const { moveInDate: moveInDateFromState } = this.state;
    const params = {
      facilityId: facility.id,
      moveInDate: moveInDate || moveInDateFromState,
      tenant,
      discountPlanId,
      autoApplyDiscountPlanIds,
      unitGroupId: tierId ? unitGroupId : id,
      softReservation: facility?.settings?.voyagerOnly || false,
      unitTierId: tierId,
    };

    const payload = {
      facilityId: facility.id,
      moveInDate: moveInDate || moveInDateFromState,
      tenantState: tenant.state,
      tenantPostal: tenant.postal,
      tenantCity: tenant.city,
      discountPlanId,
      unitGroupId: tierId ? unitGroupId : id,
      softReservation: facility?.settings?.voyagerOnly || false,
      unitTierId: tierId,
    };

    if (addEvent) {
      addEvent(dataTrackClick('reservation-submit-success'), payload, 'click');
    }

    return onSubmit(params);
  };

  handleMoveInDateChange = (moveInDate) => {
    this.setState({
      moveInDate,
    });
  };

  handlePromoUpdate = (plan) => {
    this.setState({ selectedPlan: plan });
  };

  handleCountrySelect = ({ target }) => {
    const countryInfo = getCountryInfo(target.value);
    this.setState({ ...countryInfo });
  };

  validateDate = () => {
    const { facility: { timezone } } = this.props;
    const { moveInDate } = this.state;
    // check for timezone can be removed once all facilities timezones have been shipped from FMS
    let dayInZone;
    let invalidDate;
    if (timezone) {
      dayInZone = getStartOfDayTZ(timezone);
      invalidDate = isBeforeTZ(moveInDate, dayInZone, timezone);
      return invalidDate ? `Your move in date must not be in the past in the ${timezone} timezone` : undefined;
    }

    dayInZone = getStartOfDayTZ();
    invalidDate = isBeforeTZ(moveInDate, dayInZone);

    return invalidDate ? ' Your move in date must not be in the past' : undefined;
  };

  componentDidUpdate() {
    const { error, userErrors, addEvent, facility, tierId, id, unitGroupId } = this.props;
    if (error || userErrors) {
      const userErrorList = userErrors || [];
      const errors = Array.isArray(error)
        ? [...error, ...userErrorList].filter(Boolean)
        : [error, ...userErrorList].filter(Boolean);
      const payload = {
        facilityId: facility.id,
        unitGroupId: tierId ? unitGroupId : id,
        softReservation: facility?.settings?.voyagerOnly || false,
        unitTierId: tierId,
        errors: errors.toString(),
      };
      if (addEvent) {
        addEvent(dataTrackClick('reservation-submit-error'), payload, 'click');
      }
    }
  }

  static renderErrorMessages(errors) {
    if (errors.length === 1) {
      return typeof errors[0] === 'object' ? errors[0].message : errors[0];
    }

    return (
      <ul>
        {errors.map((err, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>{typeof err === 'object' ? err.message : err}</div>
        ))}
      </ul>
    );
  }

  renderError() {
    const { error, userErrors } = this.props;

    if (!error && !userErrors) {
      return false;
    }

    const userErrorList = userErrors || [];

    const errors = Array.isArray(error)
      ? [...error, ...userErrorList].filter(Boolean)
      : [error, ...userErrorList].filter(Boolean);

    return (
      <Alert variant="danger">
        <h1>Error!!</h1>
        {this.constructor.renderErrorMessages(errors)}
      </Alert>
    );
  }

  renderDisclaimerText() {
    return get(this.props, 'facility.settings.reservationDisclaimer');
  }

  render() {
    const {
      primaryColorClass, secondaryColorBackgroundClass,
      primaryColorBackgroundHoverClass, buttonStyleClass,
      primaryColorBackgroundClass, secondaryColorHoverClass,
    } = this.context;
    const {
      isSubmitting, onSubmitSuccess, onClose, facility: {
        address: { country },
        expirationWindow,
        displayName,
        reservationWindow,
        timezone,
        useExpirationWindow,
        useReservationWindow,
        settings: { hidePricingDisplayCall, enablePublicDescription, softwareProvider },
      },
      price,
      id,
      discountedPrice,
      facility: { discountPlans, id: facilityId },
      discountPlans: discountPlansProp,
    } = this.props;

    const {
      postalValidator,
      stateName,
      postalName,
      states,
      moveInDate,
      selectedPlan,
    } = this.state;

    const minDate = getStartOfDayTZ(timezone);
    let maxDate = null;
    let moveInDateWindow = null;
    let moveInDateToolTip = null;

    if (useReservationWindow) {
      moveInDateWindow = reservationWindow;
    } else if (useExpirationWindow) {
      moveInDateWindow = expirationWindow;
    }
    if (moveInDateWindow) {
      maxDate = createDateAndAddDays(moveInDateWindow);
      moveInDateToolTip = `This facility only accepts reservations up to ${moveInDateWindow} days in
        advance.  Please select a move-in date between ${formatForReservationToolTip(minDate)} and
        ${formatForReservationToolTip(maxDate)}.`;
    }

    const countries = getCountries(country);

    return (
      <Form
        form="reservationModal"
        initialValues={{ country }}
        onSubmit={this.handleSubmit}
        onSubmitSuccess={onSubmitSuccess}
        onlyValidateOnSubmit
        validate={createValidator({
          email,
          postal: postalValidator,
        })}
      >
        <Modal.Header className={`${styles.modalHeader} ${styles.close}`} closeButton>
          <div className={`${styles.reserveTitle} ${primaryColorClass}`}>
            Reserve a Unit
          </div>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          <div className={styles.blurb}>
            <p>
              You have selected to reserve a storage unit at {displayName}.
              If you have selected the wrong size, go back and make a new
              selection.
            </p>
            {this.renderDisclaimerText()}
          </div>
          <div className={styles.subHeading}>
            Size / Features
          </div>
          <Row className={styles.descriptionWell}>
            <Col xs={12}>
              <UnitDescription
                {...this.props}
                styles={styles}
              />
            </Col>
          </Row>

          <AmountDueToday reservationFee={0} />

          <AmountDueAtMoveIn
            facilityId={facilityId}
            selectedPlan={selectedPlan}
            price={price}
            discountedPrice={discountedPrice}
            discountPlans={discountPlans}
            onPromoUpdate={this.handlePromoUpdate}
            primaryColorClass={primaryColorClass}
            primaryColorBackgroundClass={primaryColorBackgroundClass}
            secondaryColorHoverClass={secondaryColorHoverClass}
            secondaryColorBackgroundClass={secondaryColorBackgroundClass}
            primaryColorBackgroundHoverClass={primaryColorBackgroundHoverClass}
            hidePricingDisplayCall={hidePricingDisplayCall}
            enablePublicDescription={enablePublicDescription}
            softwareProvider={softwareProvider}
            appliedPromos={discountPlansProp}
            buttonStyleClass={buttonStyleClass}
          />

          {this.renderError()}

          <div className={styles.form}>
            <BasicTenantForm required />
            <Field
              className={styles.field}
              label="Address 1"
              id="reservationForm-address1"
              name="address1"
              component={Input}
              required
            />
            <Field
              className={styles.field}
              label="Address 2"
              id="reservationForm-address2"
              name="address2"
              component={Input}
            />
            <Row>
              <Col xs={12} sm={7}>
                <Field
                  className={styles.field}
                  label="City"
                  id="reservationForm-city"
                  name="city"
                  component={Input}
                  required
                />
              </Col>
              <Col xs={12} sm={5}>
                <Field
                  className={styles.field}
                  label="Country"
                  id="reservationForm-country"
                  name="country"
                  component={SelectInput}
                  htmlSelect
                  customOnChange={this.handleCountrySelect}
                  options={countries}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={8}>
                <Field
                  className={styles.field}
                  label={stateName}
                  id="reservationForm-state"
                  name="state"
                  component={SelectInput}
                  htmlSelect
                  options={states}
                  emptyOption=""
                  required
                />
              </Col>
              <Col xs={12} sm={4}>
                <Field
                  className={styles.field}
                  label={postalName}
                  id="reservationForm-postal"
                  name="postal"
                  component={Input}
                  required
                />
              </Col>
            </Row>
            <Field
              label="Move-In Date"
              id="reservationForm-moveInDate"
              name="moveInDate"
              tooltip={moveInDateToolTip}
              className={styles.field}
              component={DateInput}
              handleDateChange={this.handleMoveInDateChange}
              value={moveInDate}
              calendarContainer={document.body}
              minDate={minDate}
              maxDate={maxDate}
              validate={() => this.validateDate()}
              required
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} className={buttonStyleClass} variant="default">Cancel</Button>
          <Button
            type="submit"
            data-testid="submitReservation"
            data-event="submit-reservation-click"
            data-moveInDate={moveInDate}
            data-price={price}
            data-discountedPrice={discountedPrice}
            data-id={id}
            variant="primary"
            className={`${secondaryColorBackgroundClass} ${primaryColorBackgroundHoverClass}  ${buttonStyleClass}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Reserve'}
          </Button>
        </Modal.Footer>
      </Form>
    );
  }
}
